import { isDate, parseJSON } from 'date-fns';
import download from 'downloadjs';
import { cloneDeep, last } from 'lodash-es';
import queryString, { StringifiableRecord } from 'query-string';
import { useCallback, useMemo, useState } from 'react';
import {
  QueryKey,
  useInfiniteQuery,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient
} from 'react-query';
import { Updater } from 'react-query/types/core/utils';
import { extractGuid, useNetworkClient } from '.';
import { TAEntity } from './types';

import {
  UseMutateFunction,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from 'react-query/types/react/types';
import { ListReturnTypeResponse } from './types/generated.Framework';

export type {
  StringifiableRecord,
  UseMutateFunction,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
};

export { useInfiniteQuery, useIsMutating, useMutation, useQueryClient };

export function getCsrfHeaders(): Record<string, string> {
  const csrfInputs = document.getElementsByName('__RequestVerificationToken');
  let csrfToken = '';
  if (csrfInputs.length > 0) {
    const csrfInput = csrfInputs[0] as HTMLInputElement;
    csrfToken = csrfInput.value;
  }
  return {
    'X-CSRF-Token': csrfToken
  };
}

export function getOidcHeaders(): Record<string, string> {
  // token id: oidc.default:https://localhost:3000/authentication/callback
  // does oidc exist
  const oidcValue = sessionStorage.getItem('oidc.config_show_access_token');

  let accessToken = '';
  if (window.sessionStorage && oidcValue != undefined) {
    const session = JSON.parse(oidcValue);

    if (session['tokens'] && session['tokens']['accessToken']) {
      accessToken = `${session['tokens']['tokenType']} ${session['tokens']['accessToken']}`;
    }
  }

  return { Authorization: accessToken };
}

export function fetchCsrf(input: RequestInfo, init?: RequestInit): ReturnType<typeof fetch> {
  return fetch(input, {
    ...init,
    headers: {
      ...(init ?? {}).headers,
      ...getCsrfHeaders()
    }
  });
}

export type CancelablePromise<T> = Promise<T> & {
  cancel: () => void;
};
type WrappedQueryParameters = {
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  url: string;
  query?: StringifiableRecord;
  body?: unknown;
  headers?: Record<string, string>;
};
export function wrappedQuery<T = unknown>({
  method,
  url,
  query,
  body,
  headers
}: WrappedQueryParameters): CancelablePromise<T | undefined> {
  const endpoint = queryString.stringifyUrl({ url, query });

  let bodyStr: string | undefined;
  if (body) {
    bodyStr = JSON.stringify(transformJSON(transformDates(cloneDeep(body), 'encode'), 'encode'));
  }
  //console.log('body', body)
  const abortController = new AbortController();
  //console.log("headers", headers);
  const queryHeaders = {
    ...headers,
    ...getCsrfHeaders(),
    ...getOidcHeaders(),
    'Content-Type': 'application/json'
  };

  //console.log("queryheaders", queryHeaders);

  const promise = fetch(endpoint, {
    method,
    body: bodyStr,
    headers: queryHeaders,
    credentials: 'same-origin' as const,
    signal: abortController.signal
  })
    .then(response => Promise.all([response, response.text()]))
    .then(([response, body]) => {
      // ignore the keep alive endpoint and what it returns
      if (endpoint == '/api/v1/user-account/keep-alive') {
        body = '';
      }
      if (response.ok) {
        if ((response.headers.get('Content-Type') ?? '').indexOf('application/json') > -1)
          return body
            ? (transformDates(transformJSON(JSON.parse(body), 'decode'), 'decode') as T)
            : undefined;
        return body;
      } else if (response.status === 403) {
        throw new Error(`Access Denied for url ${endpoint}`);
      } else
        throw new Error(`Unexpected Status '${response.status}' for url ${endpoint}`, {
          cause: body
        });
      throw new Error('Unknown Network Error');
    }) as CancelablePromise<T | undefined>;

  promise.cancel = () => abortController.abort();

  return promise;
}

export function isISOString(str: string): boolean {
  return !!str.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,4})?((\+|-)\d{2}:\d{2})?Z?$/);
}

export function transformDates(obj: unknown, mode: 'encode' | 'decode'): unknown {
  if (obj && typeof obj === 'object') {
    if (isDate(obj) && obj instanceof Date && mode === 'encode') {
      console.log(obj);
      return obj.toISOString();
    } else if (Array.isArray(obj)) {
      return obj.map(item => transformDates(item, mode));
    } else {
      Object.keys(obj).forEach(key => {
        obj[key] = transformDates(obj[key], mode);
      });
    }
  } else if (obj && mode === 'decode' && typeof obj === 'string' && isISOString(obj)) {
    return parseJSON(obj);
  }
  return obj;
}

const jsonEncodedFields = ['foo'];
export function transformJSON(obj: unknown, mode: 'encode' | 'decode'): unknown {
  if (obj && typeof obj === 'object') {
    if (Array.isArray(obj)) {
      return obj.map(item => transformJSON(item, mode));
    } else {
      Object.keys(obj).forEach(key => {
        if (obj[key] && jsonEncodedFields.includes(key)) {
          obj[key] =
            mode === 'encode'
              ? typeof obj[key] === 'object'
                ? JSON.stringify(obj[key])
                : obj[key]
              : typeof obj[key] === 'string'
                ? JSON.parse(obj[key])
                : obj[key];
        } else if (obj[key] && typeof obj[key] === 'object') {
          obj[key] = transformJSON(obj[key], mode);
        }
      });
    }
  }
  return obj;
}

export type UseGetResult<T> = UseQueryResult<T | undefined, unknown> & {
  setData: (updater: Updater<T | undefined, T>) => void;
};
export function useGet<T>(
  url: string,
  extra: Omit<
    UseQueryOptions<unknown, unknown, T | undefined>,
    'queryKey' | 'queryFn' | 'enabled'
  > & {
    query?: StringifiableRecord;
    loadIfDefined?: unknown;
    key?: string;
  } = {}
): UseGetResult<T> {
  const { query, loadIfDefined, key, ...otherOptions } = extra;
  const queryClient = useQueryClient();
  const { headers } = useNetworkClient();

  const queryParams = useMemo(
    () => ({
      ...(query ?? {})
    }),
    [query]
  );

  const queryKey = useMemo(
    () => [
      ...(key ? [key] : []), // Allow for custom query key for invalidation purposes
      url,
      queryParams
    ],
    [key, url, queryParams]
  );

  return {
    ...useQuery<unknown, unknown, T | undefined>({
      queryKey,
      queryFn: () => wrappedQuery<T>({ method: 'GET', url, query: queryParams, headers }),
      enabled: Object.hasOwnProperty.call(extra, 'loadIfDefined') ? !!loadIfDefined : true,
      ...otherOptions
    }),
    setData: (updater: Updater<T | undefined, T>) => queryClient.setQueryData<T>(queryKey, updater)
  };
}

export function useSuspendingGet<T>(
  url: string,
  extra: {
    query?: StringifiableRecord;
    loadIfDefined?: unknown;
  } = {}
): T {
  const result = useGet<T>(url, {
    ...extra,
    suspense: true
  });
  if (result.isError || !result.data) throw new Error('Network error');
  return result.data;
}

export function useCreate<TData, TVariables = TData>(
  url: string,
  extra: UseMutationOptions<TData, unknown, TVariables, unknown> & {
    query?: StringifiableRecord;
    invalidateKeys?: QueryKey | QueryKey[];
  } = {}
): UseMutationResult<TData, unknown, TVariables, unknown> {
  const { query, invalidateKeys, onSuccess: extraOnSuccess, ...options } = extra;
  const { headers } = useNetworkClient();
  const queryClient = useQueryClient();

  return useMutation<TData, unknown, TVariables, unknown>({
    mutationFn: async values => {
      const result = await wrappedQuery<TData>({
        url,
        method: 'POST',
        body: values,
        query: {
          ...(query ?? {})
        },
        headers
      });

      if (!result) throw new Error(`POST returned empty value for endpoint: ${url}`);
      return result;
    },
    // Wrap onsuccess to do invalidation but then do whatever the caller wanted too
    onSuccess: (data, variables, context) => {
      if (invalidateKeys) {
        const keys = Array.isArray(invalidateKeys) ? invalidateKeys : [invalidateKeys];
        keys.forEach(key => queryClient.invalidateQueries({ queryKey: key }));
      }

      // caller onSuccess
      extraOnSuccess?.(data, variables, context);
    },
    ...options
  });
}

export function useUpdate<T, TVariables = T>(
  url: string,
  extra: UseMutationOptions<T | undefined, unknown, TVariables, unknown> & {
    query?: StringifiableRecord;
  } = {}
): UseMutationResult<T | undefined, unknown, TVariables, unknown> {
  const { query, ...options } = extra;
  const { headers } = useNetworkClient();

  return useMutation<T | undefined, unknown, TVariables, unknown>(
    values =>
      wrappedQuery<T>({
        url,
        method: 'PUT',
        body: values,
        query: {
          ...query
        },
        headers
      }),
    options
  );
}

export function useFieldUpdate<Variables = unknown>(
  url: string,
  fieldName: string,
  extra: UseMutationOptions<void, unknown, Variables, unknown> & {
    query?: StringifiableRecord;
  } = {}
): UseMutationResult<void, unknown, Variables, unknown> {
  const { query, ...options } = extra;
  const { headers } = useNetworkClient();

  return useMutation<void, unknown, Variables, unknown>(async (value: Variables) => {
    // Value could be a simple value, or it could be a full entity
    // Value may contain list of uids as opposed to uid in route.
    const { uids, ...payload } = value as {
      uids?: string[];
      [key: string]: unknown;
    };

    if (uids && Array.isArray(uids)) {
      // Bulk update
      await wrappedQuery({
        url: url,
        method: 'PATCH',
        body: {
          uids,
          ...payload // Include other fields
        },
        query: { fieldName, ...query },
        headers
      });
    } else {
      // Single update
      await wrappedQuery({
        url:
          !extractGuid(last(url.split('/'))) &&
          typeof value === 'object' &&
          !!value &&
          (value as any)['uid']
            ? `${url}/${(value as any)['uid']}`
            : url,
        method: 'PATCH',
        body:
          typeof payload === 'object' && !!payload && payload[fieldName] !== undefined
            ? { [fieldName]: payload[fieldName] }
            : { [fieldName]: value },
        query: { fieldName, ...query },
        headers
      });
    }
  }, options);
}

export function useDelete<T>(
  url: string,
  extra: UseMutationOptions<
    T | undefined,
    unknown,
    TAEntity | string | undefined | void,
    unknown
  > & {
    query?: StringifiableRecord;
  } = {}
): UseMutationResult<T | undefined, unknown, TAEntity | string | undefined | void, unknown> {
  const { query, ...options } = extra;
  const { headers } = useNetworkClient();

  return useMutation<T | undefined, unknown, TAEntity | string | undefined | void, unknown>(
    values =>
      wrappedQuery<T>({
        url: url + (values ? `/${typeof values === 'object' ? values.uid : values}` : ''),
        method: 'DELETE',
        query: {
          ...query
        },
        headers
      }),
    options
  );
}

export function usePost<TData, TVariables = TData>(
  url: string,
  extra: UseMutationOptions<TData, unknown, TVariables, unknown> & {
    query?: StringifiableRecord;
  } = {}
): UseMutationResult<TData, unknown, TVariables, unknown> {
  const { query, ...options } = extra;
  const { headers } = useNetworkClient();

  return useMutation<TData, unknown, TVariables, unknown>(async values => {
    const result = await wrappedQuery<TData>({
      url,
      method: 'POST',
      body: values,
      query: {
        ...(query ?? {})
      },
      headers
    });
    if (!result) throw new Error(`POST returned empty value for endpoint: ${url}`);
    return result;
  }, options);
}

export type useHttpReturnType = {
  get: <T>(
    endpoint: string,
    query?: StringifiableRecord,
    additionalHeaders?: Record<string, string> | undefined
  ) => Promise<T>;
  update: <T>(endpoint: string, data: T) => Promise<T | undefined>;
  patch: <T>(endpoint: string, query?: StringifiableRecord, data?: T) => Promise<T | undefined>;
  patchResponse: <T, U>(
    endpoint: string,
    query?: StringifiableRecord,
    data?: T
  ) => Promise<U | undefined>;
  updateField: (endpoint: string, fieldName: string, value: unknown) => Promise<unknown>;
  create: <T>(endpoint: string, data: Omit<T, 'uid'>) => Promise<T>;
  createRequest: <T, U>(endpoint: string, data: Omit<T, 'uid'>) => Promise<U>;
  deleteResource: (endpoint: string) => Promise<void>;
  query: <T>(params: WrappedQueryParameters) => Promise<T | undefined>;
  downloadFile: (
    endpoint: string,
    query?: StringifiableRecord,
    contentType?: string
  ) => Promise<void>;
};
export function useHttp(id?: string): useHttpReturnType {
  const queryClient = useQueryClient();
  const { headers } = useNetworkClient();
  const myId = id;

  //id && console.log(`I am ${myId} my headers are ${JSON.stringify(headers)}`);

  const get = useCallback(
    async <T>(
      endpoint: string,
      query?: StringifiableRecord,
      additionalHeaders?: Record<string, string> | undefined
    ): Promise<T> => {
      id && console.log(`I am ${myId} getting with headers ${JSON.stringify(headers)}`);

      const result = await queryClient.fetchQuery<unknown, unknown, T>([endpoint, query], {
        queryFn: () =>
          wrappedQuery({
            method: 'GET',
            url: endpoint,
            query,
            headers: { ...headers, ...additionalHeaders }
          })
      });
      return result;
    },
    [queryClient]
  );
  const { mutateAsync } = useMutation<unknown, unknown, WrappedQueryParameters>(values =>
    wrappedQuery({ ...values, headers: { ...values.headers, ...headers } })
  );
  const update = useCallback(
    async <T>(endpoint: string, data: T): Promise<T | undefined> => {
      return (await mutateAsync({
        method: 'PUT',
        url: endpoint,
        body: data
      })) as T | undefined;
    },
    [mutateAsync]
  );
  const patch = useCallback(
    async <T>(endpoint: string, query?: StringifiableRecord, data?: T): Promise<T | undefined> => {
      return (await mutateAsync({
        method: 'PATCH',
        url: endpoint,
        query,
        body: data
      })) as T | undefined;
    },
    [mutateAsync]
  );

  const patchResponse = useCallback(
    async <T, U>(
      endpoint: string,
      query?: StringifiableRecord,
      data?: T
    ): Promise<U | undefined> => {
      return (await mutateAsync({
        method: 'PATCH',
        url: endpoint,
        query,
        body: data
      })) as U | undefined;
    },
    [mutateAsync]
  );
  const updateField = useCallback(
    async (endpoint: string, fieldName: string, value: unknown): Promise<unknown> => {
      const result = await mutateAsync({
        method: 'PATCH',
        url: endpoint + '?fieldName=' + fieldName,
        body: { [fieldName]: value }
      });
      if (!result) throw new Error('CREATE request returned empty body for endpoint ' + endpoint);
      return result as unknown;
    },
    [mutateAsync]
  );
  const create = useCallback(
    async <T>(endpoint: string, data: Omit<T, 'uid'>): Promise<T> => {
      id && console.log(`I am ${myId} posting with headers ${JSON.stringify(headers)}`);

      const result = await mutateAsync({
        method: 'POST',
        url: endpoint,
        body: data
      });
      if (!result) throw new Error('CREATE request returned empty body for endpoint ' + endpoint);
      return result as T;
    },
    [mutateAsync]
  );
  const createRequest = useCallback(
    async <T, U>(endpoint: string, data: Omit<T, 'uid'>): Promise<U> => {
      const result = await mutateAsync({
        method: 'POST',
        url: endpoint,
        body: data
      });
      if (!result) throw new Error('CREATE request returned empty body for endpoint ' + endpoint);
      return result as U;
    },
    [mutateAsync]
  );
  const deleteResource = useCallback(async (endpoint: string): Promise<void> => {
    await wrappedQuery({
      method: 'DELETE',
      url: endpoint,
      headers
    });
  }, []);
  const query = useCallback(
    <T>(params: Parameters<typeof wrappedQuery>[0]): Promise<T | undefined> => {
      return wrappedQuery<T>({
        ...params,
        headers
      });
    },
    []
  );
  const downloadFile = useCallback(
    async (endpoint: string, query?: StringifiableRecord, contentType?: string): Promise<void> => {
      //await wrappedQuery({ method: 'DELETE', url: endpoint });

      if (query) endpoint = `${endpoint}?${queryString.stringify(query)}`;
      const resp = await fetch(endpoint, {
        method: 'GET',
        headers: contentType
          ? {
              Accept: contentType,
              ...headers,
              ...getCsrfHeaders(),
              ...getOidcHeaders()
            }
          : {
              ...headers,
              ...getCsrfHeaders(),
              ...getOidcHeaders()
            }
      });
      if (resp.status !== 200) {
        throw new Error((await resp.text()) ?? resp.statusText);
      }
      const blob = await resp.blob();
      const filename = resp.headers
        .get('content-disposition')
        ?.split(';')
        .filter(v => v.startsWith(' filename='))[0]
        .split('=')[1];
      download(blob, filename!.replaceAll('"', ''));
    },
    []
  );
  const returnVals = useMemo(
    () => ({
      get,
      update,
      patch,
      patchResponse,
      updateField,
      create,
      createRequest,
      deleteResource,
      query,
      downloadFile
    }),
    [
      get,
      update,
      patch,
      patchResponse,
      updateField,
      create,
      createRequest,
      deleteResource,
      query,
      downloadFile
    ]
  );
  return returnVals;
}

// Designed to work with one of our endpoints, especially a TAControllerBaseExtras endpoint
type useHttpEndpointReturnType<T> = {
  get: (uid?: string, query?: StringifiableRecord) => Promise<T>;
  list: (query?: StringifiableRecord) => Promise<Array<T>>;
  query: (query?: StringifiableRecord) => Promise<ListReturnTypeResponse<T>>;
  update: (data: T) => Promise<T | undefined>;
  patch: (query?: StringifiableRecord, data?: T) => Promise<T | undefined>;
  updateField: (data: T, fieldName: string) => Promise<unknown>;
  create: (data: Omit<T, 'uid'>) => Promise<T>;
  deleteResource: (dataOrId: T | string) => Promise<void>;
  downloadFile: (query?: StringifiableRecord, contentType?: string) => Promise<void>;
};
export function useHttpEndpoint<T extends { uid?: string | undefined }>(
  endpoint: string
): useHttpEndpointReturnType<T> {
  const http = useHttp();
  const get = useCallback(
    (uid?: string, query?: StringifiableRecord): Promise<T> => {
      return http.get<T>(endpoint + (uid ? '/' + uid : ''), query);
    },
    [endpoint, http]
  );
  const list = useCallback(
    (query?: StringifiableRecord): Promise<Array<T>> => {
      return http.get<Array<T>>(endpoint, query);
    },
    [endpoint, http]
  );
  const query = useCallback(
    (query?: StringifiableRecord): Promise<ListReturnTypeResponse<T>> => {
      return http.get<ListReturnTypeResponse<T>>(endpoint, query);
    },
    [endpoint, http]
  );
  const update = useCallback(
    (data: T): Promise<T | undefined> => {
      return http.update<T>(endpoint + '/' + data.uid, data);
    },
    [endpoint, http]
  );
  const patch = useCallback(
    (query?: StringifiableRecord, data?: T): Promise<T | undefined> => {
      return http.patch<T>(endpoint, query, data);
    },
    [endpoint, http]
  );

  const updateField = useCallback(
    (data: T, fieldName: string): Promise<unknown> => {
      return http.updateField(endpoint + '/' + data.uid, fieldName, data[fieldName]);
    },
    [endpoint, http]
  );
  const create = useCallback(
    (data: Omit<T, 'uid'>): Promise<T> => {
      return http.create(endpoint, data);
    },
    [endpoint, http]
  );
  const deleteResource = useCallback(
    (dataOrId: T | string): Promise<void> => {
      return http.deleteResource(
        endpoint + '/' + (typeof dataOrId === 'string' ? dataOrId : dataOrId.uid)
      );
    },
    [endpoint, http]
  );
  const downloadFile = useCallback(
    (query?: StringifiableRecord, contentType?: string): Promise<void> => {
      return http.downloadFile(endpoint, query, contentType);
    },
    [endpoint, http]
  );
  return { get, list, query, update, patch, updateField, create, deleteResource, downloadFile };
}

export function useFileDownload(
  url: string,
  options?: {
    filename?: string;
    contentType?: string;
    onError?: (err: Error) => void;
  }
): [() => void, boolean] {
  const [loading, setLoading] = useState<boolean>(false);
  const { headers } = useNetworkClient();

  const downloadFile = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          ...getCsrfHeaders(),
          ...getOidcHeaders(),
          ...headers,
          ...(options?.contentType ? { Accept: options.contentType } : {})
        }
      });
      if (resp.status !== 200) {
        throw new Error((await resp.text()) ?? resp.statusText);
      }
      const blob = await resp.blob();

      let filename: string = '';
      if (options?.filename) {
        filename = options.filename;
      } else if (resp.headers.get('content-disposition')) {
        filename = resp.headers.get('content-disposition')!;
        if (filename) {
          if (filename.includes('filename=')) {
            filename = filename.split('filename=')[1];
          }
          if (filename.includes('; filename*=')) {
            filename = filename.split('; filename*=')[0];
          }
        }
      }

      download(blob, filename!.replaceAll('"', ''));
    } catch (err) {
      console.error(err);
      // Due to typescript updating
      if (err instanceof Error) {
        options?.onError && options.onError(err);
      } else {
        throw err;
      }
    } finally {
      setLoading(false);
    }
  }, [setLoading, url, options]);
  return [downloadFile, loading];
}

export function useSearchFileDownload(
  url: string,
  options?: {
    query: StringifiableRecord;
    filename?: string;
    onError?: (err: Error) => void;
  }
): [(exportType: string) => void, boolean] {
  const [loading, setLoading] = useState<boolean>(false);
  const { headers } = useNetworkClient();

  const downloadFile = useCallback(
    async (exportType: string) => {
      setLoading(true);
      try {
        const aUrl = `${url}/${exportType}`;
        const query = options?.query;
        const endpoint = queryString.stringifyUrl({ url: aUrl, query });
        const contType = exportType == 'csv' ? 'text/csv' : 'application/pdf';
        const resp = await fetch(endpoint, {
          method: 'GET',
          headers: {
            ...getCsrfHeaders(),
            ...getOidcHeaders(),
            ...headers,
            ...(contType ? { Accept: contType } : {})
          }
        });
        if (resp.status !== 200) {
          throw new Error((await resp.text()) ?? resp.statusText);
        }
        const blob = await resp.blob();

        let filename: string = '';
        if (options?.filename) {
          filename = options.filename;
        } else if (resp.headers.get('content-disposition')) {
          filename = resp.headers.get('content-disposition')!;
          if (filename) {
            if (filename.includes('filename=')) {
              filename = filename.split('filename=')[1];
            }
            if (filename.includes('; filename*=')) {
              filename = filename.split('; filename*=')[0];
            }
          }
        }

        download(blob, filename!.replaceAll('"', ''));
      } catch (err) {
        console.error(err);
        // Due to typescript updating
        if (err instanceof Error) {
          options?.onError && options.onError(err);
        } else {
          throw err;
        }
      } finally {
        setLoading(false);
      }
    },
    [setLoading, url, options]
  );
  return [downloadFile, loading];
}

export function useBlobDownload(
  url: string,
  options?: { contentType?: string; onError?: (err: Error) => void }
): [() => void, boolean, Blob?] {
  const [loading, setLoading] = useState<boolean>(false);
  const [dataBlob, setDataBlob] = useState<Blob>();
  const { headers } = useNetworkClient();
  const downloadBlob = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(url, {
        method: 'GET',
        headers: options?.contentType
          ? {
              Accept: options?.contentType,
              ...getCsrfHeaders(),
              ...getOidcHeaders(),
              ...headers
            }
          : { ...getCsrfHeaders(), ...getOidcHeaders(), ...headers }
      });
      if (resp.status !== 200) {
        throw new Error((await resp.text()) ?? resp.statusText);
      }
      const blob = await resp.blob();
      setDataBlob(blob);
    } catch (err) {
      console.error(err);
      // Due to typescript updating
      if (err instanceof Error) {
        options?.onError && options.onError(err);
      } else {
        throw err;
      }
    } finally {
      setLoading(false);
    }
  }, [setLoading, url, options]);
  return [downloadBlob, loading, dataBlob];
}
